import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from 'react-toastify';
import moment from 'moment';
import { formatCurrency, formatPaymentMethod, getTransactionStatusBadgeClass } from '../../../utils/helper';
import { hasPermission } from '../../../utils/permissions';

// Each provider's merchant dashboard, matching the Settings > Payments mode so
// test transactions open the sandbox and live ones the real account.
const PROVIDER_PORTALS = {
    stripe: { live: 'https://dashboard.stripe.com/payments', test: 'https://dashboard.stripe.com/test/payments' },
    tabby:  { live: 'https://merchant.tabby.ai', test: 'https://merchant.tabby.ai' },
    tamara: { live: 'https://partners.tamara.co', test: 'https://partners-sandbox.tamara.co' },
};

const TransactionList = () => {

    const [searchParams, setSearchParams] = useSearchParams();
    const typeFilter = searchParams.get('type') || '';
    const methodFilter = searchParams.get('payment_method') || '';
    const statusFilter = searchParams.get('status') || '';
    const promoFilter = searchParams.get('promo') || '';

    const [isLoader, setIsLoader] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState({});
    const [stats, setStats] = useState(null);
    const [paymentMode, setPaymentMode] = useState('test');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Debounce the search box so each keystroke doesn't hit the API.
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            setSearchTerm(search.trim());
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        getTransactions();
    }, [page, typeFilter, methodFilter, statusFilter, promoFilter, searchTerm]);

    const getTransactions = async () => {
        try {
            setIsLoader(true);
            const response = await ApiService.request({
                method: "GET",
                url: "admin/transactions",
                data: {
                    page,
                    ...(typeFilter && { type: typeFilter }),
                    ...(methodFilter && { payment_method: methodFilter }),
                    ...(statusFilter && { status: statusFilter }),
                    ...(promoFilter && { promo: promoFilter }),
                    ...(searchTerm && { search: searchTerm }),
                },
            });
            const data = response.data;
            if (data.status) {
                setTransactions(data.data);
                setPagination(data.pagination);
                if (data.stats) setStats(data.stats);
                if (data.payment_mode) setPaymentMode(data.payment_mode);
            } else {
                toast.error(data.message);
            }
            setIsLoader(false);
        } catch (error) {
            setIsLoader(false);
        }
    };

    // Merge one filter change into the URL params, keeping the others intact.
    const applyFilters = (next) => {
        setPage(1);
        const merged = { type: typeFilter, payment_method: methodFilter, status: statusFilter, promo: promoFilter, ...next };
        setSearchParams(Object.fromEntries(Object.entries(merged).filter(([, value]) => value)));
    };

    const bucket = (key) => stats?.[key] || { count: 0, amount: 0 };
    const card = bucket('card');
    const applePay = bucket('apple_pay');

    // Header counters — amounts are what was actually paid; counts include
    // every transaction regardless of status.
    const paymentCards = [
        { label: 'Total Transactions', ...bucket('total'), icon: 'bi-receipt', color: 'text-primary' },
        {
            label: 'Card / Apple Pay',
            portal: 'stripe',
            portalName: 'Stripe',
            count: card.count + applePay.count,
            amount: card.amount + applePay.amount,
            note: applePay.count ? `incl. ${applePay.count.toLocaleString()} Apple Pay` : null,
            icon: 'bi-credit-card',
            color: 'text-info',
        },
        { label: 'Tabby', portal: 'tabby', portalName: 'Tabby', ...bucket('tabby'), icon: 'bi-wallet2', color: 'text-success' },
        { label: 'Tamara', portal: 'tamara', portalName: 'Tamara', ...bucket('tamara'), icon: 'bi-wallet', color: 'text-warning' },
    ];
    const bookingCards = [
        { label: 'App Bookings', ...bucket('app_bookings'), icon: 'bi-phone', color: 'text-primary' },
        { label: 'Walk-in Bookings', ...bucket('walkin_bookings'), icon: 'bi-person-walking', color: 'text-success' },
    ];

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Transactions</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Transactions</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-xxl">
                    {/* Summary Cards */}
                    <div className="mb-6">
                        <div className="row g-3">
                            {paymentCards.map((item) => {
                                const body = (
                                    <>
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h5 className="text-gray-600 fw-semibold mb-0">{item.label}</h5>
                                            <i className={`bi ${item.icon} fs-1 ${item.color}`}></i>
                                        </div>
                                        <h1 className="fw-bold text-dark mb-2">{item.count.toLocaleString()}</h1>
                                        <p className="mb-0 text-muted">
                                            {formatCurrency(item.amount)} paid
                                            {item.note && <span className="d-block fs-7">{item.note}</span>}
                                        </p>
                                        {item.portal && (
                                            <span className="d-inline-flex align-items-center gap-1 mt-2 fs-7 fw-semibold text-primary">
                                                Open {item.portalName}{paymentMode === 'test' ? ' (test)' : ''}
                                                <i className="bi bi-box-arrow-up-right fs-8 text-primary"></i>
                                            </span>
                                        )}
                                    </>
                                );
                                return (
                                    <div className="col-md-3" key={item.label}>
                                        {item.portal ? (
                                            <a
                                                href={PROVIDER_PORTALS[item.portal][paymentMode]}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={`Open the ${item.portalName} dashboard`}
                                                className="d-block bg-white p-4 rounded-4 shadow-sm h-100 text-reset text-decoration-none bg-hover-light-primary"
                                            >
                                                {body}
                                            </a>
                                        ) : (
                                            <div className="bg-white p-4 rounded-4 shadow-sm h-100">{body}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="row g-3 mt-0">
                            {bookingCards.map((item) => (
                                <div className="col-md-6" key={item.label}>
                                    <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <h5 className="text-gray-600 fw-semibold mb-0">{item.label}</h5>
                                            <i className={`bi ${item.icon} fs-1 ${item.color}`}></i>
                                        </div>
                                        <h1 className="fw-bold text-dark mb-2">{item.count.toLocaleString()}</h1>
                                        <p className="mb-0 text-muted">{formatCurrency(item.amount)} booking value</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="w-100">
                                <div className="row">
                                    <div className="col-md">
                                        <input
                                            type="text"
                                            className="form-control form-control-solid my-1"
                                            placeholder="Search transaction ID, booking or promo code"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={typeFilter}
                                            onChange={(e) => applyFilters({ type: e.target.value })}
                                        >
                                            <option value="">All Types</option>
                                            <option value="membership">Membership</option>
                                            <option value="booking">Booking</option>
                                        </select>
                                    </div>
                                    <div className="col-md">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={methodFilter}
                                            onChange={(e) => applyFilters({ payment_method: e.target.value })}
                                        >
                                            <option value="">All Payment Methods</option>
                                            <option value="card">Card</option>
                                            <option value="apple_pay">Apple Pay</option>
                                            <option value="tabby">Tabby</option>
                                            <option value="tamara">Tamara</option>
                                            <option value="cash">Cash</option>
                                        </select>
                                    </div>
                                    <div className="col-md">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={statusFilter}
                                            onChange={(e) => applyFilters({ status: e.target.value })}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="Paid">Paid</option>
                                            <option value="Authorized">Authorized</option>
                                            <option value="Pending">Pending</option>
                                            <option value="Refunded">Refunded</option>
                                            <option value="Voided">Voided</option>
                                        </select>
                                    </div>
                                    <div className="col-md">
                                        <select
                                            className="form-select form-select-solid my-1"
                                            value={promoFilter}
                                            onChange={(e) => applyFilters({ promo: e.target.value })}
                                        >
                                            <option value="">All Transactions</option>
                                            <option value="with">With Promo Code</option>
                                            <option value="without">Without Promo Code</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body pt-0">
                            <div className="dataTables_wrapper dt-bootstrap4 no-footer">
                                <div className="table-responsive">
                                    <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                                        <thead>
                                            <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                                <th>Transaction ID</th>
                                                <th>Customer</th>
                                                <th>Type</th>
                                                <th>Payment Method</th>
                                                <th>Amount</th>
                                                <th>Promo</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody className="fw-semibold text-gray-600 fs-6">
                                            {transactions.length > 0 ? transactions.map((txn) => (
                                                <tr key={`${txn.type}-${txn._id}`}>
                                                    <td className="text-dark text-break" style={{ maxWidth: 260 }}>{txn.transaction_id}</td>
                                                    <td>
                                                        {txn.user_id?._id ? (
                                                            <Link to={`/admin/users/${txn.user_id._id}`} className="text-dark fw-bold text-hover-primary">
                                                                {txn.user_id.name || '—'}
                                                            </Link>
                                                        ) : <span className="text-muted">Deleted user</span>}
                                                        <div className="text-muted fs-7">{txn.user_id?.email || ''}</div>
                                                    </td>
                                                    <td>
                                                        {txn.type === 'booking' ? (
                                                            <>
                                                                <div>Booking</div>
                                                                <Link to={`/admin/bookings/${txn._id}`} className="text-muted fs-7 text-hover-primary">
                                                                    {txn.reference || 'View booking'}
                                                                </Link>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div>Membership</div>
                                                                <div className="text-muted fs-7">{txn.plan_id?.title || '—'}</div>
                                                            </>
                                                        )}
                                                    </td>
                                                    <td>{formatPaymentMethod(txn.payment_method)}</td>
                                                    <td>
                                                        {txn.promo_code && txn.discount_amount > 0 && (
                                                            <div className="text-muted fs-7 text-decoration-line-through" title="Price before discount">
                                                                {formatCurrency(Number(txn.amount || 0) + Number(txn.discount_amount))}
                                                            </div>
                                                        )}
                                                        <span className="text-dark fw-bold">{formatCurrency(txn.amount)}</span>
                                                    </td>
                                                    <td>
                                                        {txn.promo_code ? (
                                                            <>
                                                                {txn.promo_code_id && hasPermission('promo-codes.show') ? (
                                                                    <Link
                                                                        to={`/admin/promo-codes/${txn.promo_code_id}`}
                                                                        className="badge badge-light-success"
                                                                        title="View promo code"
                                                                    >
                                                                        <i className="bi bi-ticket-perforated-fill text-success me-1"></i>
                                                                        {txn.promo_code}
                                                                    </Link>
                                                                ) : (
                                                                    <span className="badge badge-light-success">
                                                                        <i className="bi bi-ticket-perforated-fill text-success me-1"></i>
                                                                        {txn.promo_code}
                                                                    </span>
                                                                )}
                                                                <div className="text-success fs-7 mt-1">− {formatCurrency(txn.discount_amount)}</div>
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getTransactionStatusBadgeClass(txn.status)}`}>
                                                            {txn.status}
                                                        </span>
                                                    </td>
                                                    <td>{txn.createdAt ? moment(txn.createdAt).format('DD MMM YYYY hh:mm A') : '—'}</td>
                                                </tr>
                                            )) : (
                                                <tr>
                                                    <td colSpan="8" className="text-dark text-center mt-2">No data found!!</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                    {isLoader && (
                                        <Loader text="Loading" size="30" className="text-center" />
                                    )}

                                    {pagination.total_pages > 1 && (
                                        <Paginator
                                            page={pagination.current_page}
                                            rows={pagination.per_page}
                                            totalRecords={pagination.total_records}
                                            onPageChange={setPage}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionList;
